/**
 * Real-WebGL render-budget probe (E3.3.S1, AD-158 constraint 7) — the pure,
 * node-safe core. Like budget.ts it imports NO `three` and does no I/O.
 *
 * `estimateSceneCost` (budget.ts) scores the PLAN; this reads what the live
 * renderer actually submitted in one frame (`renderer.info`) and compares it
 * against the SAME `RENDER_BUDGETS` table — imported, never re-declared. The
 * reading is published on the DEV `__gcSceneDump` hook, where the visual lane's
 * `expect.render` block asserts it (tools/visual-lane, route `render-budget`).
 *
 * WHAT IT IS NOT: a frame-rate measurement. In CI the context is SwiftShader
 * (software); draw calls, triangles and resident geometry/texture counts are
 * the same numbers a GPU would be handed, so a resource-pool or draw-call
 * overrun is caught — how fast any GPU would draw them is not measured here.
 *
 * Presentation-only and off the per-tick path. Import boundary (AD-122):
 * frontend-local.
 */

import { checkBudget, RENDER_BUDGETS, type BudgetWarning, type RenderTier } from './budget.js';

/**
 * The subset of `renderer.info` this reads. WebGLRenderer names the per-frame
 * draw count `render.calls`; WebGPURenderer's Info names it `render.drawCalls`
 * (and keeps `calls` for a different counter), so both are accepted.
 */
export interface RendererInfoLike {
  render: { calls?: number; drawCalls?: number; triangles?: number };
  memory: { geometries?: number; textures?: number };
}

export interface RenderBudgetReading {
  tier: RenderTier;
  /** Draw calls submitted in the last full frame (every post-FX pass included). */
  drawCalls: number;
  triangles: number;
  /** Resident GPU geometries / textures — recorded; the budget table has no count for them. */
  geometries: number;
  textures: number;
  /** The table row compared against, quoted from `RENDER_BUDGETS`. */
  budget: { drawCalls: number; triangles: number };
  /** `[]` ⇒ within budget. */
  overBudget: BudgetWarning[];
}

function count(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Read one frame's totals. The caller owns the frame boundary: `Game` turns
 * `info.autoReset` off and resets once per frame, because with auto-reset on
 * every composer pass restarts the counters and only the LAST pass (a
 * full-screen quad, one call) would be read.
 */
export function readRenderBudget(info: RendererInfoLike, tier: RenderTier): RenderBudgetReading {
  const drawCalls = count(info.render.drawCalls ?? info.render.calls);
  const triangles = count(info.render.triangles);
  const budget = RENDER_BUDGETS[tier];
  return {
    tier,
    drawCalls,
    triangles,
    geometries: count(info.memory.geometries),
    textures: count(info.memory.textures),
    budget: { drawCalls: budget.drawCalls, triangles: budget.triangles },
    overBudget: checkBudget({ drawCalls, triangles }, tier),
  };
}

/**
 * Copy one frame's counters out of the live `renderer.info`, taken right after
 * the frame's render: renders issued BETWEEN frames (PMREM generation, a late
 * asset's compile) also land in the un-reset counters, and a dump read then
 * would charge them to the frame.
 */
export function snapshotRenderInfo(info: RendererInfoLike): RendererInfoLike {
  return {
    render: {
      calls: info.render.calls,
      drawCalls: info.render.drawCalls,
      triangles: info.render.triangles,
    },
    memory: { geometries: info.memory.geometries, textures: info.memory.textures },
  };
}

/**
 * The over-budget FIXTURE's size from `?budgetload=<n>` (DEV only): `n` extra
 * individually-drawn meshes, so the probe can be shown to FAIL. Absent, zero,
 * negative or non-numeric ⇒ 0; capped so a typo cannot hang a browser.
 */
export const BUDGET_LOAD_MAX = 20_000;

export function budgetLoadCount(search: string): number {
  const raw = new URLSearchParams(search).get('budgetload');
  if (raw === null) return 0;
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, BUDGET_LOAD_MAX);
}
